/**
 * ReportBuilderAPI - Smart API layer for report builder
 * 
 * Features:
 * - Request deduplication: Prevents duplicate in-flight requests
 * - Result caching: Caches successful responses to avoid redundant API calls
 * - Automatic cache invalidation: Clears cache when data source changes
 */

import { apiPost, apiGet } from "./apiClient";

interface DataValuesRequest {
  filename?: string;
  table?: string;
  column: string;
  exclude_others?: boolean;
}

interface DataValuesResponse {
  column: string;
  table?: string;
  values: string[];
  count: number;
  cached?: boolean;
}

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
}

class ReportBuilderAPIClass {
  // In-flight request tracking to prevent duplicates
  private inFlightRequests: Map<string, Promise<any>> = new Map();
  
  // Cache for successful responses
  private cache: Map<string, CacheEntry> = new Map();
  
  // Cache TTL in milliseconds (5 minutes)
  private readonly CACHE_TTL = 5 * 60 * 1000;
  
  // Current data filename
  private currentFilename: string | null = null;

  /**
   * Generate a cache key from request parameters
   */
  private getCacheKey(request: DataValuesRequest): string {
    const filename = request.filename || this.currentFilename || "current";
    const table = request.table || "global";
    const column = request.column;
    const excludeOthers = request.exclude_others !== false;
    return `${filename}:${table}:${column}:${excludeOthers}`;
  }

  /**
   * Check if a cache entry is still valid
   */
  private isCacheValid(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < this.CACHE_TTL;
  }

  /**
   * Set the current data filename (call when data source changes)
   */
  public setCurrentFilename(filename: string | null) {
    if (filename !== this.currentFilename) {
      console.log(`[ReportBuilderAPI] Data source changed: ${this.currentFilename} → ${filename}`);
      this.currentFilename = filename;
      // Clear cache when data source changes
      this.clearCache();
    }
  }

  /**
   * Clear all cached data
   */
  public clearCache() {
    console.log(`[ReportBuilderAPI] Clearing cache (${this.cache.size} entries)`);
    this.cache.clear();
  }

  /**
   * Get unique values for a column with smart caching and deduplication
   */
  public async getUniqueValues(request: DataValuesRequest): Promise<DataValuesResponse> {
    const cacheKey = this.getCacheKey(request);

    // 1. Check in-flight requests (prevent duplicate requests)
    const inFlight = this.inFlightRequests.get(cacheKey);
    if (inFlight) {
      console.log(`[ReportBuilderAPI] Deduplicating request: ${cacheKey}`);
      return inFlight;
    }

    // 2. Check cache
    const cached = this.cache.get(cacheKey);
    if (cached && this.isCacheValid(cached)) {
      console.log(`[ReportBuilderAPI] Cache hit: ${cacheKey}`);
      return { ...cached.data, cached: true };
    }

    // 3. Make API call
    console.log(`[ReportBuilderAPI] Fetching: ${cacheKey}`);
    
    // Use the resolved filename (from request or currentFilename)
    const filename = request.filename || this.currentFilename;
    
    const requestPromise = apiPost("/api/reporting/data/values/", {
      filename: filename,
      table: request.table,
      column: request.column,
      exclude_others: request.exclude_others !== false,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data: DataValuesResponse = await response.json();
        
        // Store in cache
        this.cache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });
        
        // Remove from in-flight tracking
        this.inFlightRequests.delete(cacheKey);
        
        console.log(`[ReportBuilderAPI] Cached response: ${cacheKey} (${data.count} values)`);
        return data;
      })
      .catch((error) => {
        // Remove from in-flight tracking on error
        this.inFlightRequests.delete(cacheKey);
        console.error(`[ReportBuilderAPI] Error fetching ${cacheKey}:`, error);
        throw error;
      });

    // Track in-flight request
    this.inFlightRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  /**
   * Get unique values for a column across all tables
   */
  public async getUniqueValuesGlobal(
    column: string,
    excludeOthers: boolean = true,
    filename?: string
  ): Promise<DataValuesResponse> {
    return this.getUniqueValues({
      filename,
      column,
      exclude_others: excludeOthers,
    });
  }

  /**
   * Get unique values for a specific table/column
   */
  public async getUniqueValuesForTable(
    table: string,
    column: string,
    excludeOthers: boolean = true,
    filename?: string
  ): Promise<DataValuesResponse> {
    return this.getUniqueValues({
      filename,
      table,
      column,
      exclude_others: excludeOthers,
    });
  }

  /**
   * Prefetch values for multiple columns (e.g., for branding page)
   */
  public async prefetchMultiple(
    requests: DataValuesRequest[]
  ): Promise<Map<string, DataValuesResponse>> {
    console.log(`[ReportBuilderAPI] Prefetching ${requests.length} value sets`);
    
    const results = new Map<string, DataValuesResponse>();
    
    // Execute all requests in parallel (deduplication handles any duplicates)
    await Promise.allSettled(
      requests.map(async (req) => {
        try {
          const data = await this.getUniqueValues(req);
          const key = `${req.table || ""}/${req.column}`;
          results.set(key, data);
        } catch (error) {
          console.error(`[ReportBuilderAPI] Prefetch failed for ${req.table}/${req.column}:`, error);
        }
      })
    );
    
    console.log(`[ReportBuilderAPI] Prefetch complete: ${results.size}/${requests.length} successful`);
    return results;
  }

  /**
   * Get cache statistics (for debugging)
   */
  public getCacheStats() {
    const validEntries = Array.from(this.cache.values()).filter(entry =>
      this.isCacheValid(entry)
    ).length;
    
    return {
      totalCached: this.cache.size,
      validCached: validEntries,
      inFlight: this.inFlightRequests.size,
      currentFilename: this.currentFilename,
    };
  }

  /**
   * Generic cached query method for GET endpoints
   * Supports deduplication and caching for any query
   */
  public async cachedQuery<T = any>(
    endpoint: string,
    options?: {
      ttl?: number;
      skipCache?: boolean;
    }
  ): Promise<T> {
    const cacheKey = `query:${endpoint}`;
    const ttl = options?.ttl || this.CACHE_TTL;

    // Skip cache if requested
    if (options?.skipCache) {
      console.log(`[ReportBuilderAPI] Skipping cache for: ${endpoint}`);
      const response = await apiGet(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return await response.json();
    }

    // 1. Check in-flight requests
    const inFlight = this.inFlightRequests.get(cacheKey);
    if (inFlight) {
      console.log(`[ReportBuilderAPI] Deduplicating query: ${endpoint}`);
      return inFlight as Promise<T>;
    }

    // 2. Check cache
    const cached = this.cache.get(cacheKey);
    if (cached && this.isCacheValid(cached)) {
      console.log(`[ReportBuilderAPI] Cache hit: ${endpoint}`);
      return cached.data as T;
    }

    // 3. Make API call
    console.log(`[ReportBuilderAPI] Fetching: ${endpoint}`);
    
    const requestPromise = apiGet(endpoint)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        const data = await response.json();
        
        // Store in cache
        this.cache.set(cacheKey, {
          data,
          timestamp: Date.now(),
        });
        
        // Remove from in-flight tracking
        this.inFlightRequests.delete(cacheKey);
        
        console.log(`[ReportBuilderAPI] Cached query response: ${endpoint}`);
        return data;
      })
      .catch((error) => {
        // Remove from in-flight tracking on error
        this.inFlightRequests.delete(cacheKey);
        console.error(`[ReportBuilderAPI] Error fetching ${endpoint}:`, error);
        throw error;
      });

    // Track in-flight request
    this.inFlightRequests.set(cacheKey, requestPromise);
    
    return requestPromise;
  }

  /**
   * Get schemas with caching
   */
  public async getSchemas() {
    return this.cachedQuery("/api/reporting/data/schemas");
  }

  /**
   * Get reporting periods with caching
   */
  public async getReportingPeriods() {
    return this.cachedQuery("/api/reporting/data/reporting-periods");
  }

  /**
   * Get section templates with caching
   */
  public async getSectionTemplates() {
    return this.cachedQuery("/api/reporting/templates/sections");
  }

  /**
   * Get saved reports with caching
   */
  public async getSavedReports() {
    return this.cachedQuery("/api/reporting/reports/saved");
  }

  /**
   * Get data files with caching
   */
  public async getDataFiles() {
    return this.cachedQuery("/api/reporting/data/files");
  }

  /**
   * Invalidate specific query cache
   */
  public invalidateQuery(endpoint: string) {
    const cacheKey = `query:${endpoint}`;
    this.cache.delete(cacheKey);
    console.log(`[ReportBuilderAPI] Invalidated cache for: ${endpoint}`);
  }

  /**
   * Invalidate all query caches
   */
  public invalidateAllQueries() {
    const queryKeys = Array.from(this.cache.keys()).filter(key => key.startsWith("query:"));
    queryKeys.forEach(key => this.cache.delete(key));
    console.log(`[ReportBuilderAPI] Invalidated ${queryKeys.length} query caches`);
  }
}

// Export singleton instance
export const ReportBuilderAPI = new ReportBuilderAPIClass();
